import React from 'react';
import { motion } from 'framer-motion';
import { FaRobot, FaSms, FaMobile, FaFilter, FaEnvelope, FaFileAlt, FaChartLine, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';

const features = [
  {
    id: "ai-receptionist",
    category: "AI RECEPTIONIST",
    icon: <FaRobot />,
    items: [
      { id: "voice-agent", feature: "24/7 AI Voice Agent (250 mins/mo)", problem: "Never miss a call again - even at 2 AM" },
      { id: "textback", feature: "Missed-call text-back (instant)", problem: "Claim leads before they call competitors" },
      { id: "live-transfer", feature: "Live call transfer to attorney", problem: "Hot leads go straight to you" }
    ]
  },
  {
    id: "intake",
    category: "INTAKE",
    icon: <FaMobile />,
    items: [
      { id: "hipaa-forms", feature: "Unlimited HIPAA-compliant intake forms", problem: "Capture PHI legally and securely" },
      { id: "mobile", feature: "Mobile-optimized (70% of leads are mobile)", problem: "Stop losing leads to broken forms" },
      { id: "qualification", feature: "Lead qualification logic", problem: "Filter out bad cases automatically" },
      { id: "routing", feature: "Instant case-type routing", problem: "Truck accidents get priority alerts" }
    ]
  },
  {
    id: "automation",
    category: "AUTOMATION",
    icon: <FaEnvelope />,
    items: [
      { id: "auto-respond", feature: "Auto-responder (SMS + Email)", problem: "Respond in &lt;30 seconds, every time" },
      { id: "followup", feature: "Follow-up sequences (5-touch)", problem: "No lead falls through the cracks" },
      { id: "med-records", feature: "Medical records request automation", problem: "Cases start 2 weeks faster" },
      { id: "doc-remind", feature: "Document reminder sequences", problem: "Clients submit docs without chasing" }
    ]
  },
  {
    id: "dashboard",
    category: "DASHBOARD",
    icon: <FaChartLine />,
    items: [
      { id: "pipeline", feature: "Real-time lead pipeline", problem: "See every lead's status at a glance" },
      { id: "source-track", feature: "Source tracking", problem: "Know which marketing actually works" }
    ]
  },
  {
    id: "compliance",
    category: "COMPLIANCE",
    icon: <FaShieldAlt />,
    items: [
      { id: "hipaa-infra", feature: "Full HIPAA infrastructure", problem: "Significantly reduces HIPAA compliance risk" },
      { id: "baa", feature: "Signed BAA included", problem: "Legal protection from day one" },
      { id: "encryption", feature: "Encrypted data storage", problem: "PHI protected at rest and in transit" }
    ]
  }
];

const SolutionSection = () => {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden" id="solution">
      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            The <span className="gradient-text">Apex Intake System</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A 24/7 digital intake department for less than one part-time hire.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {features.map((category, catIndex) => (
            <motion.div
              key={category.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: catIndex * 0.1 }}
              className="bg-slate-800/70 backdrop-blur-sm border border-slate-700 rounded-2xl p-8 hover-lift"
              data-testid={`feature-category-${catIndex}`}
            >
              <div className="flex items-center mb-6">
                <div className="text-3xl text-emerald-400 mr-4">{category.icon}</div>
                <h3 className="text-2xl font-bold text-emerald-400">{category.category}</h3>
              </div>

              <div className="space-y-4">
                {category.items.map((item, itemIndex) => (
                  <div key={item.id} className="flex items-start">
                    <FaCheckCircle className="text-emerald-400 mt-1 mr-3 flex-shrink-0" />
                    <div>
                      <p className="text-white font-semibold mb-1">{item.feature}</p>
                      <p className="text-gray-400 text-sm">{item.problem}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Problem → Solution → Outcome Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-slate-800/90 to-slate-900/90 backdrop-blur-sm border border-emerald-500/30 rounded-2xl p-8 overflow-x-auto"
        >
          <h3 className="text-3xl font-bold mb-8 text-center">
            Problem → Solution → <span className="gradient-text">Outcome</span>
          </h3>
          
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="py-4 px-4 text-emerald-400 font-bold">THE PROBLEM</th>
                  <th className="py-4 px-4 text-emerald-400 font-bold">THE SOLUTION</th>
                  <th className="py-4 px-4 text-emerald-400 font-bold">THE OUTCOME</th>
                </tr>
              </thead>
              <tbody className="text-gray-300">
                <tr className="border-b border-slate-700/50">
                  <td className="py-4 px-4">35% of your calls go to voicemail</td>
                  <td className="py-4 px-4">AI receptionist answers every call 24/7</td>
                  <td className="py-4 px-4 text-emerald-400 font-semibold">Capture 100% of leads</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-4 px-4">Leads call competitors while you're in court</td>
                  <td className="py-4 px-4">Instant missed-call text-back</td>
                  <td className="py-4 px-4 text-emerald-400 font-semibold">Claim leads in &lt;30 seconds</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-4 px-4">Your intake form doesn't work on phones</td>
                  <td className="py-4 px-4">Mobile-first HIPAA forms</td>
                  <td className="py-4 px-4 text-emerald-400 font-semibold">Capture the 70% of mobile leads</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-4 px-4">Staff wastes 15 hrs/week on unqualified leads</td>
                  <td className="py-4 px-4">AI qualification logic</td>
                  <td className="py-4 px-4 text-emerald-400 font-semibold">Only talk to cases worth taking</td>
                </tr>
                <tr className="border-b border-slate-700/50">
                  <td className="py-4 px-4">42% of leads come after 5 PM - you miss them</td>
                  <td className="py-4 px-4">24/7 AI + automation</td>
                  <td className="py-4 px-4 text-emerald-400 font-semibold">Work while you sleep</td>
                </tr>
                <tr>
                  <td className="py-4 px-4">A full-time intake person costs $4,000/mo</td>
                  <td className="py-4 px-4">Our complete system</td>
                  <td className="py-4 px-4 text-emerald-400 font-semibold">Same results starting at $1,500/mo per attorney</td>
                </tr>
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SolutionSection;
